import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../stores/useAuthStore';
import { useThemeStore } from '../../../stores/useThemeStore';
import { getDefaultRouteForRole, UserRole } from '../../../constants/roles';
import logoLight from '../../../assets/MainLogo/MKraftLogoLight.png';
import logoDark from '../../../assets/MainLogo/MKraftLogoDark.png';
import './Login.css';

// Import the eye icons
import { FiEye, FiEyeOff } from 'react-icons/fi';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useThemeStore();
  const { login, user } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Setup event listeners for auth events from httpClient
  useEffect(() => {
    const handleLogout = () => {
      navigate('/login', { state: { message: 'Session expired. Please login again.' } });
    };

    const handleForbidden = () => {
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute, { state: { message: 'You do not have permission to access that page.' } });
      }
    };

    window.addEventListener('auth:logout', handleLogout);
    window.addEventListener('auth:forbidden', handleForbidden);

    return () => {
      window.removeEventListener('auth:logout', handleLogout);
      window.removeEventListener('auth:forbidden', handleForbidden);
    };
  }, [navigate]);

  const handleSubmit = async () => {
    console.log('Login submitted', { email, password, rememberMe });

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    try {
      setError('');
      await login(email, password);

      // Get updated user from store
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      } else {
        navigate('/workspace');
      }
    } catch (err: any) {
      // Provide specific error messages based on error type
      if (err.status === 401) {
        setError('Invalid email or password');
      } else if (err.type === 'network') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
      console.error('Login error:', err);
    }
  };

  const handleTraineeModule = async () => {
    try {
      setError('');
      await login('trainee@example.com', 'trainee123');
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login as learner');
      console.error('Learner login error:', err);
    }
  };

  const handleAdminModule = async () => {
    try {
      setError('');
      await login('admin@example.com', 'admin123');
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login as admin');
      console.error('Admin login error:', err);
    }
  };

  const handleTrainerModule = async () => {
    try {
      setError('');
      await login('trainer@example.com', 'trainer123');
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login as trainer');
      console.error('Trainer login error:', err);
    }
  };

  const handleSuperAdminModule = async () => {
    try {
      setError('');
      await login('superadmin@example.com', 'superadmin123');
      const { user } = useAuthStore.getState();
      if (user) {
        const defaultRoute = getDefaultRouteForRole(user.role as UserRole);
        navigate(defaultRoute);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to login as super admin');
      console.error('Super Admin login error:', err);
    }
  };

  const handleForgotPassword = () => {
    navigate('/forgot-password');
  };

  // Select logo based on theme
  const logo = theme === 'dark' ? logoDark : logoLight;

  return (
    <div className="login-page-container">
      <div className="login-page-header">
        <div className="login-page-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src={logo} alt="Multiplierskraft Logo" className="login-page-logo-image" />
        </div>
      </div>

      <div className="login-page-card">
        <h1 className="login-page-title">Welcome Back!</h1>
        <p className="login-page-subtitle">Sign in to continue your learning journey</p>

        <div className="login-page-form">
          {/* Session Message (from auth events) */}
          {(location.state as any)?.message && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              color: '#92400E',
              fontSize: '14px'
            }}>
              {(location.state as any).message}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              padding: '12px',
              marginBottom: '16px',
              backgroundColor: '#FEE',
              border: '1px solid #EF4444',
              borderRadius: '8px',
              color: '#B91C1C',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          <div className="login-page-form-group">
            <label className="login-page-label" htmlFor="email">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              className="login-page-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder=""
            />
          </div>

          <div className="login-page-form-group">
            <label className="login-page-label" htmlFor="password">
              Password
            </label>
            <div className="login-page-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="login-page-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=""
              />
              <button
                type="button"
                className="login-page-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label="Toggle password visibility"
              >
                {/* Use react-icons instead of SVG */}
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </button>
            </div>
          </div>

          <div className="login-page-options">
            <label className="login-page-checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="login-page-checkbox"
              />
              <span className="login-page-checkbox-custom"></span>
              Remember Me
            </label>
            <button
              type="button"
              onClick={handleForgotPassword}
              className="login-page-forgot-link"
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            className="login-page-submit-button"
          >
            Sign In
          </button>

          {/* Quick Access Buttons */}
          <div style={{
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid var(--color-border, #E5E7EB)'
          }}>
            <p style={{
              textAlign: 'center',
              marginBottom: '12px',
              color: 'var(--color-text-secondary, #6B7280)',
              fontSize: '14px'
            }}>
              Quick Access (Demo)
            </p>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <button
                type="button"
                onClick={handleTraineeModule}
                className="login-page-signup-button"
              >
                Learner
              </button>
              <button
                type="button"
                onClick={handleAdminModule}
                className="login-page-signup-button"
              >
                Admin
              </button>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px'
            }}>
              <button
                type="button"
                onClick={handleTrainerModule}
                className="login-page-signup-button"
              >
                Trainer
              </button>
              <button
                type="button"
                onClick={handleSuperAdminModule}
                className="login-page-signup-button"
              >
                Super Admin
              </button>
            </div>
          </div>
        </div>

        <div className="login-page-footer">
          Need Help?{' '}
          <a href="#support" className="login-page-support-link">
            Connect with Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
